import { useCallback, useEffect, useMemo, useState } from 'react';
import excelIcon from '@/assets/excel-icon.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Send } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Download,
  Eye,
  ArrowRight,
  Loader2,
  Save,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { RFIItem, RFIStatus, RFIPriority } from '@/types/rfiItem';
import { useRFI } from '@/context/RFIContext';
import { ProjectHeader } from '@/components/ProjectHeader';
import { downloadRfiExcel, listRfiRequests } from '@/lib/api/rfi';
import { getProjectWithRfi } from '@/lib/api/projects';
import { useProjectRole, NO_PERMISSION_TOOLTIP } from '@/hooks/useProjectRole';
import { BackendError } from '@/lib/backend';
import type { RFIRequestListItem } from '@/types/backend';
import { RFIGrid } from '@/components/rfi/RFIGrid';
import { NextStepsDialog } from '@/components/rfi/NextStepsDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getDeepResearchResult, startDeepResearch } from '@/lib/api/projects';

const statusConfig: Record<RFIStatus, { label: string; dot: string }> = {
  'Open': { label: 'Open', dot: 'bg-foreground/40' },
  'In Progress': { label: 'In Progress', dot: 'bg-foreground/60' },
  'Received': { label: 'Received', dot: 'bg-foreground' },
  'Provided': { label: 'Provided', dot: 'bg-foreground/80' },
  'Closed': { label: 'Closed', dot: 'bg-foreground' },
};

const priorityConfig: Record<RFIPriority, { label: string }> = {
  'Critical': { label: 'Critical' },
  'High': { label: 'High' },
  'Medium': { label: 'Medium' },
  'Low': { label: 'Low' },
};

function mapBackendPriority(p: RFIRequestListItem["priority"]): RFIPriority {
  switch (p) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
  }
}

function mapBackendStatus(s: RFIRequestListItem["status"]): RFIStatus {
  switch (s) {
    case "open":
      return "Open";
    case "in_progress":
      return "In Progress";
    case "closed":
      return "Received";
  }
}

function mapBackendCategory(t: RFIRequestListItem["type"]): string {
  switch (t) {
    case "business_context":
      return "Business context";
    case "historical_trading":
      return "Historical trading";
    case "historical_balance_sheet_and_cash_flows":
      return "Historical balance sheet and cash flows";
    case "forecast_trading":
      return "Forecast trading";
  }
}

function formatBackendDate(date: string | null | undefined): string {
  if (!date) return "";

  // Prefer stable editable format for round-tripping (YYYY-MM-DD).
  if (/^\d{4}-\d{2}-\d{2}/.test(date)) return date.slice(0, 10);

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toISOString().slice(0, 10);
}

function mapBackendItemToUi(item: RFIRequestListItem): RFIItem {
  return {
    id: String(item.id),
    ref: item.ref,
    priority: mapBackendPriority(item.priority),
    status: mapBackendStatus(item.status),
    date: formatBackendDate(item.date),
    area: item.area,
    documentRef: item.document_ref,
    request: item.requests,
    managementComment: item.management_comment ?? "",
    advisorNotes: item.advisor_notes ?? "",
    category: mapBackendCategory(item.type),
  };
}

type TrackerStep = "tracker" | "processing" | "deep-research";

const RFITracker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const isUploadMode = searchParams.get("mode") === "upload";
  const isRevisedRfiMode = window.location.pathname === "/revised-rfi";
  const { canEdit } = useProjectRole(projectId);

  // Fetch project data for header
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectId ? getProjectWithRfi(projectId) : Promise.reject('No project ID'),
    enabled: !!projectId,
  });

  const { rfiItems: contextItems, uploadedDocuments, updateRFIStatus, updateRFIComment } = useRFI();
  const [items, setItems] = useState<RFIItem[]>(isUploadMode ? [] : contextItems);
  const [itemsSource, setItemsSource] = useState<"context" | "backend">("context");
  const [step, setStep] = useState<TrackerStep>("tracker");
  const [showNextStepsDialog, setShowNextStepsDialog] = useState(false);
  const [sendEmailAfterResearch, setSendEmailAfterResearch] = useState(false);
  const [hasUnsavedRFIChanges, setHasUnsavedRFIChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [rfiSaveState, setRfiSaveState] = useState<{
    handleSave: () => Promise<void>;
    hasUnsavedChanges: boolean;
    isSaving: boolean;
    justSaved: boolean;
  } | null>(null);
  
  const [filterStatus, setFilterStatus] = useState<RFIStatus | 'All'>('All');
  const [filterPriority, setFilterPriority] = useState<RFIPriority | 'All'>('All');
  const [filterCategory, setFilterCategory] = useState<string>('All');
  

  useEffect(() => {
    if (isUploadMode) {
      setItems([]);
      return;
    }
    if (itemsSource === "backend") return;
    setItems(contextItems);
  }, [contextItems, itemsSource, isUploadMode]);

  useEffect(() => {
    if (!projectId || isUploadMode) return;

    let cancelled = false;
    (async () => {
      try {
        const rows = await listRfiRequests(projectId);
        if (cancelled) return;
        setItems(rows.map(mapBackendItemToUi));
        setItemsSource("backend");
      } catch (e) {
        if (cancelled) return;
        const message =
          e instanceof BackendError
            ? `${e.message} (HTTP ${e.status})`
            : e instanceof Error
              ? e.message
              : "Unknown error";
        toast({
          title: "Failed to load RFI requests",
          description: message,
          variant: "destructive",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, isUploadMode]);

  const categories = useMemo(() => {
    const cats = new Set(items.map(item => item.category));
    return ['All', ...Array.from(cats)];
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (filterStatus !== 'All' && item.status !== filterStatus) return false;
      if (filterPriority !== 'All' && item.priority !== filterPriority) return false;
      if (filterCategory !== 'All' && item.category !== filterCategory) return false;
      return true;
    });
  }, [items, filterStatus, filterPriority, filterCategory]);

  const stats = useMemo(() => {
    const total = items.length;
    const received = items.filter(i => i.status === 'Received').length;
    const inProgress = items.filter(i => i.status === 'In Progress').length;
    const open = items.filter(i => i.status === 'Open').length;
    const outstanding = open + inProgress;
    const progress = total > 0 ? (received / total) * 100 : 0;
    return { total, received, inProgress, open, outstanding, progress };
  }, [items]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, typeof filteredItems> = {};
    filteredItems.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredItems]);

  const handleStatusChange = (id: string, status: RFIStatus) => {
    if (itemsSource === "backend") {
      setItems(prev => prev.map(item => (item.id === id ? { ...item, status } : item)));
    } else {
      updateRFIStatus(id, status);
    }
    toast({ title: 'Status updated' });
  };

  const handleCommentChange = (id: string, comment: string) => {
    if (itemsSource === "backend") {
      setItems(prev => prev.map(item => (item.id === id ? { ...item, managementComment: comment } : item)));
    } else {
      updateRFIComment(id, comment);
    }
  };

  const handleAdvisorNotesChange = (id: string, notes: string) => {
    // пока нет PATCH эндпоинта — держим локально в UI (и для backend, и для context)
    setItems(prev => prev.map(item => (item.id === id ? { ...item, advisorNotes: notes } : item)));
  };

  const handleExport = async () => {
    if (!projectId) {
      toast({
        title: "No active project selected",
        description: "Please open the tracker from a project.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadRfiExcel(projectId);
    } catch (e) {
      const message =
        e instanceof BackendError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : "Unknown error";
      toast({
        title: "Export failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const goToDatabook = useCallback(() => {
    if (!projectId) {
      toast({
        title: "No active project selected",
        description: "Please open the tracker from a project.",
        variant: "destructive",
      });
      return;
    }
    navigate(`/databook-builder?projectId=${encodeURIComponent(projectId)}`);
  }, [navigate, projectId]);

  const goToRfiEmail = useCallback(() => {
    if (!projectId) {
      toast({
        title: "No active project selected",
        description: "Please open the tracker from a project.",
        variant: "destructive",
      });
      return;
    }
    navigate(`/rfi-email?projectId=${encodeURIComponent(projectId)}`);
  }, [navigate, projectId]);
  const handleGenerateExtras = useCallback(async (options: { research: boolean; email: boolean }) => {
    setShowNextStepsDialog(false);

    if (!options.research && !options.email) {
      goToDatabook();
      return;
    }

    if (!projectId) {
      toast({
        title: "No active project selected",
        description: "Please open the tracker from a project.",
        variant: "destructive",
      });
      return;
    }

    // If user only wants email, go straight to the separate email page.
    if (options.email && !options.research) {
      goToRfiEmail();
      return;
    }

    setStep("processing");

    let researchReady = options.research;
    if (options.research) {
      try {
        // Only start if it doesn't exist yet.
        await getDeepResearchResult(projectId);
      } catch (e) {
        if (e instanceof BackendError && e.status === 404) {
          try {
            await startDeepResearch(projectId);
          } catch (startErr) {
            const message =
              startErr instanceof BackendError
                ? `${startErr.message} (HTTP ${startErr.status})`
                : startErr instanceof Error
                  ? startErr.message
                  : "Unknown error";
            toast({
              title: "Failed to start Deep Research",
              description: message,
              variant: "destructive",
            });
            researchReady = false;
          }
        } else {
          const message =
            e instanceof BackendError
              ? `${e.message} (HTTP ${e.status})`
              : e instanceof Error
                ? e.message
                : "Unknown error";
          toast({
            title: "Failed to check Deep Research status",
            description: message,
            variant: "destructive",
          });
          researchReady = false;
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 2500));

    setSendEmailAfterResearch(options.email);

    if (researchReady) {
      setStep("deep-research");
    } else {
      setStep("tracker");
    }
  }, [goToDatabook, goToRfiEmail, projectId]);

  const handleSkip = useCallback(() => {
    setShowNextStepsDialog(false);
    goToDatabook();
  }, [goToDatabook]);

  const handleDeepResearchContinue = useCallback(() => {
    if (sendEmailAfterResearch) {
      goToRfiEmail();
      return;
    }
    goToDatabook();
  }, [sendEmailAfterResearch, goToDatabook, goToRfiEmail]);

  const handleConfirmDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    setHasUnsavedRFIChanges(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  const handleCancelDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ProjectHeader
        title={projectData?.name}
        backTo={projectId ? `/databook-builder?projectId=${encodeURIComponent(projectId)}` : "/databook-builder"}
      />

      <main className="container mx-auto px-8 py-8">
        {step === "processing" && (
          <Card className="border-border">
            <CardContent className="py-16 text-center">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-foreground/40 mb-4" />
              <p className="text-base font-medium text-foreground mb-1">Generating materials...</p>
              <p className="text-sm text-muted-foreground">
                Preparing company research...
              </p>
            </CardContent>
          </Card>
        )}

        {step === "deep-research" && (
          <div className="space-y-4 animate-fade-in h-full">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-foreground tracking-tight">
                Deep Research
              </h1>
              <Button onClick={handleDeepResearchContinue} size="sm" className="gap-2">
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <Card className="border-border">
              <CardContent className="py-10">
                <p className="text-sm font-medium text-foreground">
                  Deep Research runs in the background
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Open the Deep Research page to check status or refresh the result.
                </p>

                <div className="mt-5 flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => {
                      if (!projectId) return;
                      navigate(`/deep-research?projectId=${encodeURIComponent(projectId)}`);
                    }}
                    disabled={!projectId}
                  >
                    <Eye className="w-4 h-4" />
                    Open Deep Research
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === "tracker" && (
          <>

        {isRevisedRfiMode && (
          <h1 className="mb-6 text-2xl font-semibold tracking-tight text-foreground">Revised RFI</h1>
        )}


        {/* Stats Summary - 4 separate cards */}
        <div className="grid grid-cols-4 gap-3 mb-3">
          <Card className="border-border">
            <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
              <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.total}</span>
              <p className="text-[11px] leading-[1.5] text-muted-foreground">Total Items</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.open}</span>
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />
              </div>
              <p className="text-[11px] leading-[1.5] text-muted-foreground">Open</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.inProgress}</span>
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/70" />
              </div>
              <p className="text-[11px] leading-[1.5] text-muted-foreground">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex h-9 items-center justify-between gap-2.5 px-3 py-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xl leading-[1.3] font-semibold text-foreground">{stats.received}</span>
                <div className="w-1.5 h-1.5 rounded-full bg-foreground" />
              </div>
              <p className="text-[11px] leading-[1.5] text-muted-foreground">Received</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center justify-end gap-3 mb-5">
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.xlsx,.xls,.csv';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    toast({ title: 'RFI uploaded', description: `${file.name} has been uploaded successfully.` });
                  }
                };
                input.click();
              }}
            >
              <img src={excelIcon} alt="Excel" className="w-4 h-4" />
              Upload RFI
            </Button>
            {rfiSaveState && canEdit && (
              <Button
                onClick={rfiSaveState.handleSave}
                disabled={rfiSaveState.justSaved || rfiSaveState.isSaving}
                variant={rfiSaveState.hasUnsavedChanges ? "default" : "outline"}
                size="sm"
                className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
              >
                {rfiSaveState.justSaved && !rfiSaveState.isSaving ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Saved
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    {rfiSaveState.isSaving ? 'Saving...' : 'Save changes'}
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
              onClick={handleExport}
              disabled={!projectId}
            >
              <img src={excelIcon} alt="Excel" className="w-4 h-4" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 h-8 px-2.5 text-[11px] leading-[1.5] [&_svg]:size-3.5"
              onClick={() => {
                if (!projectId) {
                  toast({ title: "No active project selected", variant: "destructive" });
                  return;
                }
                toast({ title: "RFI sent to Management", description: "The vendor portal has been updated with the latest RFI items." });
              }}
              disabled={!projectId || !canEdit}
              title={!canEdit ? NO_PERMISSION_TOOLTIP : undefined}
            >
              <Send className="w-3.5 h-3.5" />
              Send to Mgmt
            </Button>
          </div>
        </div>

        {/* RFI Grid (same component/style as /rfi) */}
        <RFIGrid
          projectId={isUploadMode ? undefined : projectId ?? undefined}
          filterStatus={filterStatus}
          filterPriority={filterPriority}
          filterCategory={filterCategory}
          onUnsavedChangesChange={setHasUnsavedRFIChanges}
          showEmailSection={false}
          onExposeSave={setRfiSaveState}
          canEdit={canEdit}
        />

        {/* Filters above affect the grid rows shown; saving still syncs the full underlying list safely. */}
        <NextStepsDialog
          open={showNextStepsDialog}
          onOpenChange={setShowNextStepsDialog}
          onGenerate={handleGenerateExtras}
          onSkip={handleSkip}
        />

        <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes to the RFI. Are you sure you want to leave without saving?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelDiscard}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDiscard}>Discard changes</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </>
        )}
      </main>
    </div>
  );
};

export default RFITracker;
